import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { ExpertsApplyModel } from "../../../../domain/ExpertsApplyFormModel";
import { useDispatch, useSelector } from "react-redux";
import { authSelector, messageThreadsSelector } from "../../../../redux/selectors";
import { Controller, useForm } from "react-hook-form";
import { Error } from "../../../common/Error";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { getMessageThreads, getUnreadMessageCount, markMessagesAsRead, sendPublicMessage } from "../../../../redux/actions";
import React, { useEffect, useState } from "react";
import { expertsApplyService } from "../../../../services";
import { MessageThread, PublicMessage } from "@ofqual/portal-activity-feed/activityfeed/domain/PublicMessageModel";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import ReactMarkdown from "react-markdown";
import { Activity } from "@ofqual/portal-activity-feed";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { Skeleton } from "@material-ui/lab";
import { ActivityType } from "../../../../domain/ActivityType";

const useStyles = makeStyles((_) => ({
  actions: {
    padding: 15,
  },
}));

export const SendPublicMessageDialog: React.FC<{
  onClose: () => void;
  specialist: ExpertsApplyModel;
  activity?: Activity | undefined;
}> = ({ onClose, specialist, activity }) => {
  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      message: null,
      messageFiles: [],
      subject: null,
      citizenValidation: false,
      messageThreadId: undefined,
    },
  });

  const buildGetBlob = (blobId: string, messageId: string) => () =>
    expertsApplyService.downloadActivityAttachment(
      specialist.id,
      messageId,
      blobId,
      {
        token,
      }
    );

  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useSelector(authSelector);
  const [messageThread, setMessageThread] = useState<MessageThread>();
  const [disableForm, setDisableForm] = useState<boolean>(false);

  let threadId: string | undefined = undefined;
  let message: PublicMessage | undefined = undefined;
  if (activity !== undefined) {
    message = JSON.parse(activity?.activityMetaData) as PublicMessage;
    threadId = message.messageThreadId;
  }

  useEffect(() => {
    if (messageThread?.threadActivityType === ActivityType.SpecialistFurtherEvidenceRequested) {
      setDisableForm(true);
    }
  }, [messageThread])

  useEffect(() => {
    dispatch(getMessageThreads(specialist.id));

  }, [dispatch, specialist]);

  const messagesState = useSelector(messageThreadsSelector);
  useEffect(() => {

    if (messagesState.status !== "LOADING" && messagesState.hasData) {
      let mts: MessageThread[] = Object.values(messagesState.messageThreads)[0] as unknown as MessageThread[];
      setMessageThread(mts.find((x) => x.threadId === threadId));
    }
  }, [messagesState, specialist, threadId, setMessageThread]);

  useEffect(() => {
    messageThread !== undefined && dispatch(markMessagesAsRead(messageThread));
  }, [dispatch, messageThread])

  const CloseAndUpdateThings = () => {
    dispatch(getUnreadMessageCount(specialist.id));
    onClose();
  }

  const handleSendMessage = (data: {
    files: File[];
    message: string;
    subject: string;
    responseTypeId: number;
    formalResponseTypeId?: number;
    threadId: string;
  }) => {
    dispatch(
      sendPublicMessage(specialist.id, data.files, message === undefined ? data.subject : message.subject, data.message, threadId)
    );

    CloseAndUpdateThings();
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">Contact the citizen</DialogTitle>
      <Divider />
      {messagesState.status !== "LOADING" ? (
        <>
          {!disableForm && (
            <DialogContent style={{ minHeight: "350px" }}>
              <form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      rules={{ required: true }}
                      name="citizenValidation"
                      render={({ onChange }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              data-test="citizen-response-checkbox"
                              onChange={(e) => onChange(e.target.checked)}
                            />
                          }
                          label="I understand that this communication is directed at, and visible to, the citizen *"
                          labelPlacement="start"
                          // data-test="citizen-response-checkbox-error"
                          style={{
                            color: errors.citizenValidation ? "red" : undefined,
                          }}
                        />
                      )}
                    />
                    <Divider />
                  </Grid>
                  {threadId === undefined && (
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="subject"
                        rules={{ required: true, maxLength: 255 }}
                        render={({ onChange, value }) => (
                          <TextField
                            onChange={onChange}
                            value={value}
                            data-test="case-note-subject-field"
                            label="Subject"
                            required
                          />
                        )}
                        required
                      />
                      {errors.subject?.type === "maxLength" && (
                        <Error data-test="public-message-subject-length-validation">
                          Subject must be less than 255 characters
                        </Error>
                      )}
                      {errors.subject?.type === "required" && (
                        <Error data-test="public-message-subject-field-error">
                          Enter a subject for the message
                        </Error>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="message"
                      rules={{ required: true, maxLength: 5000 }}
                      render={({ onChange, value }) => (
                        <MarkdownEditor
                          onChange={onChange}
                          value={value}
                          dataTest="create-note-text-field"
                        />
                      )}
                    />
                    {errors.message?.type === "maxLength" && (
                      <Error data-test="public-message-message-length-validation">
                        Message must be less than 5000 characters
                      </Error>
                    )}
                    {errors.message?.type === "required" && (
                      <Error data-test="public-message-message-field-error">
                        Enter a message
                      </Error>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="files"
                      as={
                        <FileInput
                          multiple={true}
                          dataTest="public-message-add-attachment-button"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </form>
            </DialogContent>
          )}
          {!disableForm && (
            <DialogActions className={classes.actions}>
              <Button
                onClick={CloseAndUpdateThings}
                color="primary"
                data-test="close-add-note-dialog"
              >
                Close
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit(handleSendMessage)}
                data-test="add-note-message-button"
              >
                Send
              </Button>
            </DialogActions>
          )}

          {threadId !== undefined && messageThread !== undefined && (
            <>
              <DialogContent>
                {messageThread?.messages!.map((message, index) => (
                  <React.Fragment key={index}>
                    <div
                      data-test={`message-thread-${index}`}
                      key={message.id}
                      style={{
                        borderRadius: "5px",
                        border: "1px solid #D3D3D3",
                        padding: "10px",
                      }}
                    >
                      {message.createdByDisplayName}
                      <div style={{ float: "right" }}>
                        {formatUtcDate(
                          message.createdDate,
                          standardDateFormats.DATE_TIME
                        )}
                      </div>
                      <hr></hr>
                      <b>{message.subject}</b>
                      <ReactMarkdown>{message.message}</ReactMarkdown>
                      {message.documents.map((doc) => (
                        <li key={doc.blobId} style={{ listStyleType: "none" }}>
                          {
                            <DownloadLink
                              fileName={doc.fileName}
                              getBlob={buildGetBlob(doc.blobId, message.id)}
                            />
                          }
                        </li>
                      ))}
                    </div>

                    <br></br>
                    <br></br>
                  </React.Fragment>
                ))}
              </DialogContent>
              {disableForm && (
                <DialogActions>
                  <Button
                    onClick={CloseAndUpdateThings}
                    color="primary"
                    data-test="close-add-note-dialog"
                  >
                    Close
                  </Button>
                </DialogActions>
              )}
            </>
          )}
        </>
      ) : (
        <DialogContent style={{ minHeight: "350px" }}>
          <Skeleton variant="text" height={60} />
          <br />
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={60} />
          <br />
          <Skeleton variant="rect" height={118} />
        </DialogContent>
      )}

    </Dialog>
  );
};
